import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Loader2, Sprout, MapPin, Phone, Tractor, Award, Leaf } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FarmerApplication = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Farm Information
    farmName: '',
    farmerName: '',
    farmType: '',
    farmSize: '',
    farmingExperience: '',
    
    // Location
    village: '',
    district: '',
    state: '',
    country: 'India',
    pincode: '',
    gpsCoordinates: '',
    
    // Crops & Products
    primaryCrops: '',
    seasonalAvailability: '',
    annualProduction: '',
    organicCertified: false,
    certifications: '',
    
    // Infrastructure
    irrigationType: '',
    packingFacility: false,
    coldStorage: false,
    transportAvailable: false,
    
    // Contact Information
    contactName: '',
    email: '',
    phone: '',
    whatsapp: '',
    preferredLanguage: '',
    
    // Banking
    bankName: '',
    bankBranch: '',
    accountType: '',
    
    // Additional
    currentBuyers: '',
    expectedPrice: '',
    additionalInfo: '',
    referredBy: ''
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const applicationData = {
        farmName: formData.farmName,
        farmerName: formData.farmerName,
        farmType: formData.farmType,
        farmSize: formData.farmSize,
        farmingExperience: formData.farmingExperience || null,
        village: formData.village,
        district: formData.district,
        state: formData.state,
        country: formData.country,
        pincode: formData.pincode || null,
        gpsCoordinates: formData.gpsCoordinates || null,
        primaryCrops: formData.primaryCrops ? formData.primaryCrops.split(',').map(s => s.trim()) : [],
        seasonalAvailability: formData.seasonalAvailability || null,
        annualProduction: formData.annualProduction || null,
        organicCertified: formData.organicCertified,
        certifications: formData.certifications ? formData.certifications.split(',').map(s => s.trim()) : [],
        irrigationType: formData.irrigationType || null,
        packingFacility: formData.packingFacility,
        coldStorage: formData.coldStorage,
        transportAvailable: formData.transportAvailable,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone,
        whatsapp: formData.whatsapp || null,
        preferredLanguage: formData.preferredLanguage || null,
        bankName: formData.bankName || null,
        bankBranch: formData.bankBranch || null,
        accountType: formData.accountType || null,
        currentBuyers: formData.currentBuyers || null,
        expectedPrice: formData.expectedPrice || null,
        additionalInfo: formData.additionalInfo || null,
        referredBy: formData.referredBy || null
      };

      const response = await axios.post(`${API_URL}/api/apply/farmer`, applicationData);
      
      if (response.data.success) {
        toast({
          title: "Application Submitted Successfully!",
          description: response.data.message,
        });

        // Reset form
        setFormData({
          farmName: '',
          farmerName: '',
          farmType: '',
          farmSize: '',
          farmingExperience: '',
          village: '',
          district: '',
          state: '',
          country: 'India',
          pincode: '',
          gpsCoordinates: '',
          primaryCrops: '',
          seasonalAvailability: '',
          annualProduction: '',
          organicCertified: false,
          certifications: '',
          irrigationType: '',
          packingFacility: false,
          coldStorage: false,
          transportAvailable: false,
          contactName: '',
          email: '',
          phone: '',
          whatsapp: '',
          preferredLanguage: '',
          bankName: '',
          bankBranch: '',
          accountType: '',
          currentBuyers: '',
          expectedPrice: '',
          additionalInfo: '',
          referredBy: ''
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error submitting your application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const farmTypes = [
    'Family Farm',
    'Farmer Cooperative',
    'FPO (Farmer Producer Organization)',
    'Contract Farming Group',
    'Individual Farmer',
    'Agricultural Company'
  ];

  const irrigationTypes = [
    'Drip Irrigation',
    'Sprinkler System',
    'Canal Irrigation',
    'Well / Borewell',
    'River/Pond',
    'Rainfed'
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
    'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
    'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
  ];

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/verified-directory" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Verified Directory
            </Link>
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                  alt="SPANXPORT" 
                  className="h-12"
                />
                <span className="text-2xl text-slate-400">×</span>
                <div className="flex items-center gap-2">
                  <Sprout className="w-8 h-8 text-green-600" />
                  <span className="text-2xl font-bold text-green-700">ORIVANA Fresh</span>
                </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#263776]">Become a Partner Farmer</h1>
              <p className="text-lg md:text-xl text-slate-700">
                Join our network of verified farmers and get direct access to premium export markets
              </p>
            </div>
            
            {/* Benefits */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { icon: <Award className="w-6 h-6" />, text: 'Premium Prices' },
                { icon: <Tractor className="w-6 h-6" />, text: 'Training & Support' },
                { icon: <Leaf className="w-6 h-6" />, text: 'Certification Help' },
                { icon: <Phone className="w-6 h-6" />, text: 'Direct Payments' }
              ].map((item, idx) => (
                <div key={idx} className="bg-white/80 rounded-lg p-4 text-center shadow-sm">
                  <div className="text-green-600 flex justify-center mb-2">{item.icon}</div>
                  <span className="text-sm font-medium text-slate-700">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="farmer-application-form">
                {/* Farm Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6 flex items-center gap-2">
                    <Tractor className="w-6 h-6 text-green-600" />
                    Farm Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="farmName">Farm Name *</Label>
                      <Input
                        id="farmName"
                        name="farmName"
                        placeholder="e.g., Krishna Farm"
                        value={formData.farmName}
                        onChange={handleChange}
                        required
                        data-testid="farm-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="farmerName">Farmer / Owner Name *</Label>
                      <Input
                        id="farmerName"
                        name="farmerName"
                        value={formData.farmerName}
                        onChange={handleChange}
                        required
                        data-testid="farmer-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="farmType">Type of Farm *</Label>
                      <select
                        id="farmType"
                        name="farmType"
                        value={formData.farmType}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                        data-testid="farm-type-select"
                      >
                        <option value="">Select farm type</option>
                        {farmTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="farmSize">Total Farm Size *</Label>
                      <Input
                        id="farmSize"
                        name="farmSize"
                        placeholder="e.g., 25 acres or 10 hectares"
                        value={formData.farmSize}
                        onChange={handleChange}
                        required
                        data-testid="farm-size-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="farmingExperience">Years of Farming Experience</Label>
                      <Input
                        id="farmingExperience"
                        name="farmingExperience"
                        type="number"
                        placeholder="e.g., 15"
                        value={formData.farmingExperience}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6 flex items-center gap-2">
                    <MapPin className="w-6 h-6 text-green-600" />
                    Farm Location
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="village">Village / Town *</Label>
                      <Input
                        id="village"
                        name="village"
                        value={formData.village}
                        onChange={handleChange}
                        required
                        data-testid="village-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="district">District *</Label>
                      <Input
                        id="district"
                        name="district"
                        value={formData.district}
                        onChange={handleChange}
                        required
                        data-testid="district-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State *</Label>
                      <select
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                        data-testid="state-select"
                      >
                        <option value="">Select state</option>
                        {indianStates.map((state) => (
                          <option key={state} value={state}>{state}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="pincode">Pincode</Label>
                      <Input
                        id="pincode"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        maxLength={6}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="gpsCoordinates">GPS Coordinates (optional)</Label>
                      <Input
                        id="gpsCoordinates"
                        name="gpsCoordinates"
                        placeholder="e.g., 18.5204, 73.8567"
                        value={formData.gpsCoordinates}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Crops & Products */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6 flex items-center gap-2">
                    <Sprout className="w-6 h-6 text-green-600" />
                    Crops & Production
                  </h2>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <Label htmlFor="primaryCrops">Primary Crops / Products *</Label>
                      <Textarea
                        id="primaryCrops"
                        name="primaryCrops"
                        placeholder="e.g., Grapes (Thompson, Sonaka), Mangoes (Alphonso), Onions"
                        value={formData.primaryCrops}
                        onChange={handleChange}
                        rows={2}
                        required
                        data-testid="primary-crops-input"
                      />
                      <p className="text-sm text-slate-500 mt-1">Separate multiple crops with commas</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="annualProduction">Annual Production Capacity</Label>
                        <Input
                          id="annualProduction"
                          name="annualProduction"
                          placeholder="e.g., 50 tonnes"
                          value={formData.annualProduction}
                          onChange={handleChange}
                        />
                      </div>
                      <div>
                        <Label htmlFor="seasonalAvailability">Seasonal Availability</Label>
                        <Input
                          id="seasonalAvailability"
                          name="seasonalAvailability"
                          placeholder="e.g., Grapes: Feb-May, Mangoes: Apr-Jun"
                          value={formData.seasonalAvailability}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="certifications">Certifications (if any)</Label>
                      <Textarea
                        id="certifications"
                        name="certifications"
                        placeholder="e.g., Organic India, GlobalGAP, APEDA Registered"
                        value={formData.certifications}
                        onChange={handleChange}
                        rows={2}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="organicCertified"
                        checked={formData.organicCertified}
                        onCheckedChange={(checked) => handleCheckboxChange('organicCertified', checked)}
                      />
                      <Label htmlFor="organicCertified" className="cursor-pointer">
                        My farm is Organic Certified
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Infrastructure */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Infrastructure & Facilities</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="irrigationType">Irrigation Type</Label>
                      <select
                        id="irrigationType"
                        name="irrigationType"
                        value={formData.irrigationType}
                        onChange={handleChange}
                        className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                      >
                        <option value="">Select irrigation type</option>
                        {irrigationTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-4">
                      <Label>Available Facilities</Label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="packingFacility"
                            checked={formData.packingFacility}
                            onCheckedChange={(checked) => handleCheckboxChange('packingFacility', checked)}
                          />
                          <Label htmlFor="packingFacility" className="cursor-pointer">Packing / Grading Facility</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="coldStorage"
                            checked={formData.coldStorage}
                            onCheckedChange={(checked) => handleCheckboxChange('coldStorage', checked)}
                          />
                          <Label htmlFor="coldStorage" className="cursor-pointer">Cold Storage Access</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="transportAvailable"
                            checked={formData.transportAvailable}
                            onCheckedChange={(checked) => handleCheckboxChange('transportAvailable', checked)}
                          />
                          <Label htmlFor="transportAvailable" className="cursor-pointer">Own Transport Available</Label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6 flex items-center gap-2">
                    <Phone className="w-6 h-6 text-green-600" />
                    Contact Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="contactName">Contact Person Name *</Label>
                      <Input
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Mobile Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="e.g., 9876543210"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="whatsapp">WhatsApp Number</Label>
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        type="tel"
                        placeholder="Same as mobile or different"
                        value={formData.whatsapp}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferredLanguage">Preferred Language</Label>
                      <select
                        id="preferredLanguage"
                        name="preferredLanguage"
                        value={formData.preferredLanguage}
                        onChange={handleChange}
                        className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                      >
                        <option value="">Select language</option>
                        <option value="Hindi">Hindi</option>
                        <option value="English">English</option>
                        <option value="Marathi">Marathi</option>
                        <option value="Gujarati">Gujarati</option>
                        <option value="Tamil">Tamil</option>
                        <option value="Telugu">Telugu</option>
                        <option value="Kannada">Kannada</option>
                        <option value="Punjabi">Punjabi</option>
                        <option value="Bengali">Bengali</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Banking Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Banking Information (for Payments)</h2>
                  <p className="text-sm text-slate-600 mb-4">Optional - can be provided later after verification</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bankBranch">Branch</Label>
                      <Input
                        id="bankBranch"
                        name="bankBranch"
                        value={formData.bankBranch}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="accountType">Account Type</Label>
                      <select
                        id="accountType"
                        name="accountType"
                        value={formData.accountType}
                        onChange={handleChange}
                        className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                      >
                        <option value="">Select account type</option>
                        <option value="Savings">Savings</option>
                        <option value="Current">Current</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Additional Information</h2>
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="currentBuyers">Current Buyers / Market</Label>
                      <Textarea
                        id="currentBuyers"
                        name="currentBuyers"
                        placeholder="Who do you currently sell to? e.g., Local mandi, APMC, Traders, Direct buyers"
                        value={formData.currentBuyers}
                        onChange={handleChange}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="expectedPrice">Expected Price Range</Label>
                      <Input
                        id="expectedPrice"
                        name="expectedPrice"
                        placeholder="e.g., ₹50-70 per kg for grapes"
                        value={formData.expectedPrice}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="additionalInfo">Any Other Information</Label>
                      <Textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        placeholder="Any additional details about your farm, products, or requirements"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="referredBy">How did you hear about us?</Label>
                      <Input
                        id="referredBy"
                        name="referredBy"
                        placeholder="e.g., Another farmer, Agriculture officer, Online"
                        value={formData.referredBy}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <p className="text-sm text-green-800">
                      <strong>What happens next?</strong> Our field team will review your application and may visit your farm within 5-7 business days. 
                      After verification, you'll be added to our Partner Farmer Network with access to premium export markets.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="bg-green-600 text-white hover:bg-green-700"
                      disabled={isSubmitting}
                      data-testid="submit-farmer-application"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Sprout className="mr-2 h-4 w-4" />
                          Submit Application
                        </>
                      )}
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <Link to="/verified-directory">Cancel</Link>
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FarmerApplication;
